import { forwardRef, type CSSProperties, type MouseEventHandler, type PointerEventHandler, type ReactNode } from 'react';
import { type Drill, type ExercisePitch } from '../types';
import { FIELD_WIDTH, getFieldHeight, getDrillOrientation } from '../lib/field';

interface FieldViewProps {
  drill?: Drill;
  pitch?: ExercisePitch;
  variant?: 'indoor' | 'outdoor';
  preset?: 'full' | 'half' | 'threequarters';
  orientation?: 'right' | 'left';
  children?: ReactNode;
  onClick?: MouseEventHandler<SVGSVGElement>;
  onPointerMove?: PointerEventHandler<SVGSVGElement>;
  onPointerUp?: PointerEventHandler<SVGSVGElement>;
  onPointerLeave?: PointerEventHandler<SVGSVGElement>;
  className?: string;
  style?: CSSProperties;
}

export const FieldView = forwardRef<SVGSVGElement, FieldViewProps>(function FieldView(
  {
    drill,
    pitch: pitchProp,
    variant: variantProp,
    preset: presetProp,
    orientation: orientationProp,
    children,
    onClick,
    onPointerMove,
    onPointerUp,
    onPointerLeave,
    className,
    style,
  }: FieldViewProps,
  ref
) {
  const pitch = pitchProp || drill?.pitch;
  const variant = pitch?.variant || variantProp || drill?.variant || 'outdoor';
  const practiceArea = Boolean(pitch) || variant === 'indoor';
  const preset = presetProp || 'threequarters';
  const orientation = orientationProp || getDrillOrientation(drill);

  const fieldWidth = FIELD_WIDTH;
  const fieldHeight = practiceArea ? 800 : getFieldHeight(preset);

  const goalWidth = 200;
  const goalDepth = 20;
  const circleRadius = 145 * 2;
  const innerCircleRadius = 75 * 3;
  const backlineY = 0;
  const circleCenterY = backlineY;
  const line23Y = fieldHeight / 2;

  const combinedStyle: CSSProperties = {
    maxHeight: '80vh',
    ...(style ?? {}),
  };

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${fieldWidth} ${fieldHeight}`}
      className={`w-full h-full ${className ?? ''}`}
      style={combinedStyle}
      preserveAspectRatio="xMidYMid meet"
      data-orientation={orientation}
      data-variant={variant}
      onClick={onClick}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
    >
      <rect x="0" y="0" width={fieldWidth} height={fieldHeight} fill={variant === 'indoor' ? '#286483' : '#3a7d3a'} />

      <rect x="0" y="0" width={fieldWidth} height={fieldHeight} fill="none" stroke="#fff" strokeWidth="3" />

      {!practiceArea && <>
      <rect
        x={(fieldWidth - goalWidth) / 2}
        y={-10}
        width={goalWidth}
        height={goalDepth}
        fill="none"
        stroke="#fff"
        strokeWidth="3"
      />

      <line x1="0" y1={line23Y} x2={fieldWidth} y2={line23Y} stroke="#fff" strokeWidth="2" />

      <circle cx={fieldWidth / 2} cy={circleCenterY} r={innerCircleRadius} fill="none" stroke="#fff" strokeWidth="2" />

      <circle
        cx={fieldWidth / 2}
        cy={circleCenterY}
        r={circleRadius}
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        strokeDasharray="8,6"
      />

      <circle cx={fieldWidth / 2} cy={circleCenterY} r={4} fill="#fff" />

      {preset === 'full' && (
        <>
          <line x1="0" y1={fieldHeight / 2} x2={fieldWidth} y2={fieldHeight / 2} stroke="#fff" strokeWidth="2" />
          <circle cx={fieldWidth / 2} cy={fieldHeight / 2} r={circleRadius} fill="none" stroke="#fff" strokeWidth="2" />
          <rect
            x={(fieldWidth - goalWidth) / 2}
            y={fieldHeight - 10}
            width={goalWidth}
            height={goalDepth}
            fill="none"
            stroke="#fff"
            strokeWidth="3"
          />
          <line x1="0" y1={fieldHeight - line23Y} x2={fieldWidth} y2={fieldHeight - line23Y} stroke="#fff" strokeWidth="2" />
          <circle cx={fieldWidth / 2} cy={fieldHeight - line23Y} r={circleRadius} fill="none" stroke="#fff" strokeWidth="2" />
          <circle
            cx={fieldWidth / 2}
            cy={fieldHeight - line23Y}
            r={innerCircleRadius}
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeDasharray="8,6"
          />
        </>
      )}

      {preset === 'threequarters' && (
        <line x1="0" y1={fieldHeight - 100} x2={fieldWidth} y2={fieldHeight - 100} stroke="#fff" strokeWidth="2" />
      )}

      </>}
      {(pitch?.boards || variant === 'indoor') && <g data-testid="indoor-sideboards" stroke="#493329" strokeWidth={12}><line x1={6} y1={0} x2={6} y2={800} /><line x1={994} y1={0} x2={994} y2={800} /></g>}
      <g className="field-content">{children}</g>
    </svg>
  );
});
